'use client';

import Image from 'next/image';
import { useState, useEffect } from 'react';
import { 
  ArrowLeft, ArrowRight, Film, LayoutGrid, Square, Expand, X 
} from 'lucide-react';

export interface GalleryImage {
  id: string;
  src: string;
  alt?: string;
  isPrimary?: boolean;
}

export interface StoneInvestmentPropertyGalleryProps {
  images: GalleryImage[];
  propertyName?: string;
  locale?: string;
}

type GalleryStyle = 'MASONRY' | 'STRIP' | 'THEATER';

// 1. THE STRIP: Horizontal Scroll with Parallax
const GalleryStrip = ({ 
  images, 
  isFullscreen, 
  onImageClick 
}: { 
  images: GalleryImage[]; 
  isFullscreen?: boolean;
  onImageClick?: (index: number) => void;
}) => (
  <div className={`flex gap-4 md:gap-8 overflow-x-auto no-scrollbar snap-x snap-mandatory ${
    isFullscreen ? 'h-screen items-center px-12 bg-black' : 'py-4'
  }`}>
    {images.map((img, i) => (
      <div 
        key={img.id} 
        onClick={() => onImageClick && onImageClick(i)}
        className={`shrink-0 snap-center relative overflow-hidden group cursor-pointer ${
          isFullscreen 
            ? 'h-[80vh] w-auto aspect-video' 
            : 'w-[85vw] md:w-[60vw] aspect-video'
        }`}
      >
        <Image 
          src={img.src}
          alt={img.alt || `Gallery ${i}`}
          fill
          className="object-cover transition-transform duration-[2000ms] ease-out group-hover:scale-110"
        />
        {!isFullscreen && (
          <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-500" />
        )}
        
        <span className={`absolute left-6 text-white text-xs uppercase tracking-widest transition-all duration-500 font-sans ${
          isFullscreen 
            ? 'bottom-6 opacity-100' 
            : 'bottom-6 opacity-0 translate-y-4 group-hover:opacity-100 group-hover:translate-y-0'
        }`}>
          0{i + 1}
        </span>
      </div>
    ))}
  </div>
);

// 2. THE MASONRY: Asymmetric Editorial Grid
const GalleryMasonry = ({ 
  images, 
  isFullscreen, 
  onImageClick 
}: { 
  images: GalleryImage[]; 
  isFullscreen?: boolean;
  onImageClick?: (index: number) => void;
}) => (
  <div className={`grid gap-4 bg-stone-100 ${
    isFullscreen 
      ? 'grid-cols-1 md:grid-cols-4 auto-rows-[40vh] p-6 md:p-12 h-screen overflow-y-auto bg-stone-900' 
      : 'grid-cols-1 md:grid-cols-4 auto-rows-[300px] md:auto-rows-[400px]'
  }`}>
    {/* Image 0 - Large Square */}
    {images[0] && (
      <div 
        onClick={() => onImageClick && onImageClick(0)} 
        className="md:col-span-2 md:row-span-2 relative overflow-hidden group cursor-pointer"
      >
        <Image src={images[0].src} alt={images[0].alt || ''} fill className="object-cover transition-transform duration-[2000ms] group-hover:scale-105" />
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
      </div>
    )}
    
    {/* Image 1 - Tall */}
    {images[1] && (
      <div 
        onClick={() => onImageClick && onImageClick(1)} 
        className="md:col-span-1 md:row-span-2 relative overflow-hidden group cursor-pointer"
      >
        <Image src={images[1].src} alt={images[1].alt || ''} fill className="object-cover transition-transform duration-[2000ms] group-hover:scale-105" />
      </div>
    )}

    {/* Image 2 - Std */}
    {images[2] && (
      <div 
        onClick={() => onImageClick && onImageClick(2)} 
        className="md:col-span-1 md:row-span-1 relative overflow-hidden group cursor-pointer"
      >
        <Image src={images[2].src} alt={images[2].alt || ''} fill className="object-cover transition-transform duration-[2000ms] group-hover:scale-105" />
      </div>
    )}
    
    {/* Image 3 - Std */}
    {images[3] && (
      <div 
        onClick={() => onImageClick && onImageClick(3)} 
        className="md:col-span-1 md:row-span-1 relative overflow-hidden group cursor-pointer"
      >
        <Image src={images[3].src} alt={images[3].alt || ''} fill className="object-cover transition-transform duration-[2000ms] group-hover:scale-105" />
      </div>
    )}

    {/* Image 4 - Wide */}
    {images[4] && (
      <div 
        onClick={() => onImageClick && onImageClick(4)} 
        className="md:col-span-4 md:row-span-1 relative overflow-hidden group cursor-pointer"
      >
        <Image src={images[4].src} alt={images[4].alt || ''} fill className="object-cover transition-transform duration-[2000ms] group-hover:scale-105" />
      </div>
    )}
    
    {/* In Fullscreen, list remaining images if any */}
    {isFullscreen && images.slice(5).map((img, i) => (
      <div 
        key={img.id} 
        onClick={() => onImageClick && onImageClick(i + 5)} 
        className="md:col-span-1 md:row-span-1 relative overflow-hidden group cursor-pointer"
      >
        <Image src={img.src} alt={img.alt || ''} fill className="object-cover" />
      </div>
    ))}
  </div>
);

// 3. THE THEATER: Single Focus with Fade
const GalleryTheater = ({ 
  images, 
  isFullscreen, 
  startIndex = 0 
}: { 
  images: GalleryImage[]; 
  isFullscreen?: boolean;
  startIndex?: number;
}) => {
  const [currentIndex, setCurrentIndex] = useState(startIndex);

  useEffect(() => {
    setCurrentIndex(startIndex);
  }, [startIndex]);

  const next = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setCurrentIndex((prev) => (prev + 1) % images.length);
  };

  const prev = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  return (
    <div className={`relative w-full group bg-stone-900 ${
      isFullscreen ? 'h-screen flex items-center justify-center' : 'aspect-video md:aspect-[21/9]'
    }`}>
      {/* Main Image */}
      {images.map((img, idx) => (
        <div 
          key={img.id}
          className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
            idx === currentIndex ? 'opacity-100 z-10' : 'opacity-0 z-0'
          }`}
        >
          <Image 
            src={img.src} 
            alt={img.alt || ''} 
            fill
            className={`${isFullscreen ? 'object-contain' : 'object-cover opacity-90'}`}
          />
        </div>
      ))}

      {/* Controls */}
      <div className={`absolute inset-0 z-20 flex items-center justify-between p-4 md:p-12 transition-opacity duration-500 ${
        isFullscreen ? 'opacity-0 hover:opacity-100' : 'opacity-0 group-hover:opacity-100'
      }`}>
        <button 
          onClick={prev} 
          className="p-4 rounded-full bg-white/10 hover:bg-white text-white hover:text-stone-900 backdrop-blur-sm transition-all"
        >
          <ArrowLeft size={20} />
        </button>
        <button 
          onClick={next} 
          className="p-4 rounded-full bg-white/10 hover:bg-white text-white hover:text-stone-900 backdrop-blur-sm transition-all"
        >
          <ArrowRight size={20} />
        </button>
      </div>

      {/* Counter */}
      <div className="absolute bottom-8 left-12 z-20">
        <span className="font-serif text-4xl text-white italic">{currentIndex + 1}</span>
        <span className="text-white/50 text-sm ml-2 font-sans">/ {images.length}</span>
      </div>
    </div>
  );
};

/**
 * Stone Investment Property Gallery
 * 
 * Concept One Design (Exact Match):
 * - Three gallery modes: STRIP, MASONRY, THEATER
 * - Style switcher with icons
 * - Fullscreen mode
 * - Image zoom on hover
 */
export function StoneInvestmentPropertyGallery({
  images,
  propertyName = 'Property',
  locale: _locale = 'en', // Reserved for i18n
}: StoneInvestmentPropertyGalleryProps) {
  const [galleryStyle, setGalleryStyle] = useState<GalleryStyle>('MASONRY');
  const [isGalleryFullscreen, setIsGalleryFullscreen] = useState(false);
  const [fullscreenStartIndex, setFullscreenStartIndex] = useState(0);

  const handleImageClick = (index: number) => {
    setFullscreenStartIndex(index);
    setIsGalleryFullscreen(true);
  };

  if (!images || images.length === 0) {
    return null;
  }

  return (
    <>
      <section className="relative w-full py-12 md:py-24 bg-stone-100 overflow-hidden">
        {/* Gallery Controls */}
        <div className="px-6 md:px-24 mb-12 flex flex-col md:flex-row justify-between items-end border-b border-stone-200 pb-4">
          <div>
            <span className="text-stone-400 uppercase tracking-[0.3em] text-xs font-sans">Visual Narrative</span>
            <h3 className="font-serif text-3xl md:text-4xl text-stone-900 mt-2">The Gallery</h3>
          </div>
          <div className="flex gap-2 mt-4 md:mt-0 items-center">
            <button 
              onClick={() => setGalleryStyle('STRIP')}
              className={`p-2 rounded border transition-colors ${
                galleryStyle === 'STRIP' 
                  ? 'bg-stone-900 text-white border-stone-900' 
                  : 'text-stone-400 border-stone-300 hover:text-stone-900'
              }`}
              title="Strip View"
            >
              <Film size={16} />
            </button>
            <button 
              onClick={() => setGalleryStyle('MASONRY')}
              className={`p-2 rounded border transition-colors ${
                galleryStyle === 'MASONRY' 
                  ? 'bg-stone-900 text-white border-stone-900' 
                  : 'text-stone-400 border-stone-300 hover:text-stone-900'
              }`}
              title="Masonry View"
            >
              <LayoutGrid size={16} />
            </button>
            <button 
              onClick={() => setGalleryStyle('THEATER')}
              className={`p-2 rounded border transition-colors ${
                galleryStyle === 'THEATER' 
                  ? 'bg-stone-900 text-white border-stone-900' 
                  : 'text-stone-400 border-stone-300 hover:text-stone-900'
              }`}
              title="Theater View"
            >
              <Square size={16} />
            </button>
            {/* Maximize Button */}
            <div className="w-px h-6 bg-stone-300 mx-2" />
            <button 
              onClick={() => { setFullscreenStartIndex(0); setIsGalleryFullscreen(true); }}
              className="p-2 rounded border border-stone-300 text-stone-400 hover:text-stone-900 hover:border-stone-900 transition-colors"
              title="Fullscreen"
            >
              <Expand size={16} />
            </button>
          </div>
        </div>

        {/* Render Active Gallery Inline */}
        <div className="opacity-0 animate-[fadeUp_1s_ease-out_forwards]">
          {galleryStyle === 'STRIP' && (
            <div className="pl-6 md:pl-24">
              <GalleryStrip images={images} onImageClick={handleImageClick} />
            </div>
          )}
          {galleryStyle === 'MASONRY' && (
            <div className="px-6 md:px-24">
              <GalleryMasonry images={images} onImageClick={handleImageClick} />
            </div>
          )}
          {galleryStyle === 'THEATER' && (
            <div className="px-0">
              <GalleryTheater images={images} />
          </div>
        )}
      </div>
      </section>

      {/* --- FULLSCREEN GALLERY OVERLAY --- */}
      {isGalleryFullscreen && (
        <div className="fixed inset-0 z-[100] bg-stone-950 flex flex-col opacity-0 animate-[fadeUp_0.5s_ease-out_forwards]">
          {/* Fullscreen Header */}
          <div className="absolute top-0 left-0 w-full p-6 flex justify-between items-center z-50 mix-blend-difference text-white">
            <div>
              <span className="font-serif italic text-xl">{propertyName}</span>
              <span className="mx-3 text-white/50">|</span>
              <span className="text-xs uppercase tracking-widest text-white/70 font-sans">Gallery</span>
            </div>
            
            <div className="flex items-center gap-6">
              {/* Style Switcher in Fullscreen */}
              <div className="flex gap-2 bg-white/10 p-1 rounded-lg backdrop-blur-md">
                <button
                  onClick={() => setGalleryStyle('STRIP')} 
                  className={`p-2 rounded transition-colors ${
                    galleryStyle === 'STRIP' ? 'bg-white text-stone-900' : 'text-white/60 hover:text-white'
                  }`}
                >
                  <Film size={16}/>
                </button>
                <button
                  onClick={() => setGalleryStyle('MASONRY')} 
                  className={`p-2 rounded transition-colors ${
                    galleryStyle === 'MASONRY' ? 'bg-white text-stone-900' : 'text-white/60 hover:text-white'
                  }`}
                >
                  <LayoutGrid size={16}/>
                </button>
                <button 
                  onClick={() => setGalleryStyle('THEATER')} 
                  className={`p-2 rounded transition-colors ${
                    galleryStyle === 'THEATER' ? 'bg-white text-stone-900' : 'text-white/60 hover:text-white'
                  }`}
                >
                  <Square size={16}/>
                </button>
            </div>

              <button 
                onClick={() => setIsGalleryFullscreen(false)}
                className="w-10 h-10 rounded-full border border-white/30 flex items-center justify-center hover:bg-white hover:text-stone-900 transition-all"
              >
                <X size={20} />
              </button>
            </div>
            </div>

          {/* Fullscreen Content */}
          <div className="flex-1 w-full h-full">
            {galleryStyle === 'STRIP' && <GalleryStrip images={images} isFullscreen={true} />}
            {galleryStyle === 'MASONRY' && <GalleryMasonry images={images} isFullscreen={true} />}
            {galleryStyle === 'THEATER' && <GalleryTheater images={images} isFullscreen={true} startIndex={fullscreenStartIndex} />}
          </div>
              </div>
            )}
    </>
  );
}
